import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { APP_REGISTRY, GROUP_COLORS as REGISTRY_COLORS } from "../../lib/appRegistry";
import { useAuth } from "../../context/AuthContext";

const GROUP_COLORS: Record<string, string> = {
  ...REGISTRY_COLORS,
  Command: "#34d399",
};

interface SpotlightResult {
  id: string;
  label: string;
  icon: string;
  group: string;
  type: "app" | "command" | "recent";
  action?: () => void;
}

interface SpotlightLauncherProps {
  onNavigate: (id: string) => void;
  onClose: () => void;
  minimizeAll: () => void;
  tileHorizontally: () => void;
  cascadeWindows: () => void;
  closeAllWindows: () => void;
  recentWindows: string[];
}

const RECENT_STORAGE_KEY = "opentool-spotlight-recent";

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecent(ids: string[]) {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids.slice(0, 10)));
  } catch { /* ignore */ }
}

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  // Substring match first
  if (t.includes(q)) return true;
  // Fuzzy: all chars of query appear in order
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  // Exact prefix gets highest score
  if (t.startsWith(q)) return 100;
  // Contains substring
  if (t.includes(q)) return 80;
  // Fuzzy match — score by density
  let qi = 0;
  let lastMatch = -1;
  let gaps = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatch >= 0 && ti - lastMatch > 1) gaps += ti - lastMatch - 1;
      lastMatch = ti;
      qi++;
    }
  }
  if (qi < q.length) return 0;
  return Math.max(1, 60 - gaps * 5);
}

export function SpotlightLauncher({
  onNavigate,
  onClose,
  minimizeAll,
  tileHorizontally,
  cascadeWindows,
  closeAllWindows,
  recentWindows,
}: SpotlightLauncherProps) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [recent] = useState<string[]>(() => loadRecent());

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => document.addEventListener("mousedown", onClick), 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  // Build commands list
  const commands: SpotlightResult[] = useMemo(() => [
    {
      id: "cmd-minimize-all",
      label: "Minimize All",
      icon: "M3 19h18",
      group: "Command",
      type: "command" as const,
      action: minimizeAll,
    },
    {
      id: "cmd-show-desktop",
      label: "Show Desktop",
      icon: "M3 19h18 M5 19V8l7-5 7 5v11",
      group: "Command",
      type: "command" as const,
      action: minimizeAll,
    },
    {
      id: "cmd-tile-windows",
      label: "Tile Windows",
      icon: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z",
      group: "Command",
      type: "command" as const,
      action: tileHorizontally,
    },
    {
      id: "cmd-cascade",
      label: "Cascade Windows",
      icon: "M2 3h16v12H2z M6 7h16v12H6z",
      group: "Command",
      type: "command" as const,
      action: cascadeWindows,
    },
    {
      id: "cmd-close-all",
      label: "Close All Windows",
      icon: "M18 6L6 18 M6 6l12 12",
      group: "Command",
      type: "command" as const,
      action: closeAllWindows,
    },
    {
      id: "cmd-toggle-theme",
      label: "Toggle Theme",
      icon: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z",
      group: "Command",
      type: "command" as const,
      action: () => window.dispatchEvent(new CustomEvent("opentool-toggle-personalization")),
    },
  ], [minimizeAll, tileHorizontally, cascadeWindows, closeAllWindows]);

  // Build app results
  const appResults: SpotlightResult[] = useMemo(() =>
    APP_REGISTRY
      .filter((a) => !a.adminOnly || user?.admin)
      .map((a) => ({
        id: a.id,
        label: a.label,
        icon: a.icon,
        group: a.group,
        type: "app" as const,
      })),
    [user],
  );

  // Build recent results (shown when search is empty)
  const recentResults: SpotlightResult[] = useMemo(() => {
    // Merge localStorage recent and live recent windows
    const combined = [...new Set([...recentWindows, ...recent])];
    return combined.slice(0, 3).map((id) => {
      const app = APP_REGISTRY.find((a) => a.id === id);
      return app ? {
        id: app.id,
        label: app.label,
        icon: app.icon,
        group: "Recent",
        type: "recent" as const,
      } : null;
    }).filter(Boolean) as SpotlightResult[];
  }, [recentWindows, recent]);

  // Filter and sort results
  const results: SpotlightResult[] = useMemo(() => {
    if (!search.trim()) {
      // Show recent + all apps
      return [...recentResults, ...appResults.slice(0, 8)];
    }

    const q = search.trim();
    const scored: { result: SpotlightResult; score: number }[] = [];

    for (const app of appResults) {
      const labelScore = fuzzyScore(q, app.label);
      const idScore = fuzzyScore(q, app.id);
      const groupScore = fuzzyScore(q, app.group);
      const best = Math.max(labelScore, idScore, groupScore);
      if (best > 0 || fuzzyMatch(q, app.label) || fuzzyMatch(q, app.id)) {
        scored.push({ result: app, score: best || 1 });
      }
    }

    for (const cmd of commands) {
      const labelScore = fuzzyScore(q, cmd.label);
      if (labelScore > 0 || fuzzyMatch(q, cmd.label)) {
        scored.push({ result: cmd, score: labelScore || 1 });
      }
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.result);
  }, [search, appResults, commands, recentResults]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIdx(0);
  }, [results.length, search]);

  // Scroll selected into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const selected = resultsRef.current.querySelector(".spotlight-result--selected");
    if (selected) {
      selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIdx]);

  const executeResult = useCallback((result: SpotlightResult) => {
    if (result.type === "command" && result.action) {
      result.action();
    } else {
      // Track in recent
      const updated = [result.id, ...recent.filter((r) => r !== result.id)];
      saveRecent(updated);
      onNavigate(result.id);
    }
    onClose();
  }, [onNavigate, onClose, recent]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selectedIdx]) {
        executeResult(results[selectedIdx]);
      }
    }
  }, [results, selectedIdx, executeResult]);

  return (
    <div className="spotlight-overlay">
      <div className="spotlight-container" ref={containerRef}>
        <div className="spotlight-search">
          <svg className="spotlight-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="spotlight-search__input"
            placeholder="Search apps, commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="spotlight-search__kbd">ESC</kbd>
        </div>

        {results.length > 0 && (
          <div className="spotlight-results" ref={resultsRef}>
            {results.map((result, idx) => (
              <button
                key={result.id + "-" + result.type}
                className={`spotlight-result ${idx === selectedIdx ? "spotlight-result--selected" : ""}`}
                onClick={() => executeResult(result)}
                onMouseEnter={() => setSelectedIdx(idx)}
              >
                <div className="spotlight-result__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d={result.icon} />
                  </svg>
                </div>
                <span className="spotlight-result__label">{result.label}</span>
                <span
                  className="spotlight-result__badge"
                  style={{ color: GROUP_COLORS[result.group] || "#6b7280" }}
                >
                  {result.group}
                </span>
                {idx === selectedIdx && (
                  <span className="spotlight-result__hint">
                    <kbd>↵</kbd>
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {results.length === 0 && search.trim() && (
          <div className="spotlight-empty">
            No results for &ldquo;{search}&rdquo;
          </div>
        )}
      </div>
    </div>
  );
}
